export type ImplantCatalogOption = {
  _id: string
  name: string
  brand: string
  source_code?: string
  is_favorite?: boolean
  usage_count?: number
}

export type ImplantCatalogOptionGroup = {
  key: 'favorites' | 'popular' | 'all'
  label: string
  options: ImplantCatalogOption[]
}

export const DEFAULT_POPULAR_IMPLANT_LIMIT = 8

/**
 * Normalizzazione condivisa dalla combobox: preserva il testo leggibile ma
 * rende la ricerca indipendente da maiuscole, accenti e spazi ripetuti.
 */
export function normalizeImplantSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it-IT')
    .replace(/\s+/g, ' ')
    .trim()
}

function compactSearch(value: string): string {
  return normalizeImplantSearch(value).replace(/[^a-z0-9]/g, '')
}

export function matchesImplantCatalogOption(
  option: ImplantCatalogOption,
  searchTerm: string
): boolean {
  const normalizedTerm = normalizeImplantSearch(searchTerm)
  if (!normalizedTerm) return true

  const searchable = normalizeImplantSearch(
    `${option.name} ${option.brand} ${option.source_code ?? ''}`
  )
  if (searchable.includes(normalizedTerm)) return true

  // Consente di trovare un codice sia con sia senza punti/trattini.
  const compactTerm = compactSearch(searchTerm)
  return compactTerm.length > 0 && compactSearch(searchable).includes(compactTerm)
}

function usageCount(option: ImplantCatalogOption): number {
  const count = option.usage_count ?? 0
  return Number.isFinite(count) && count > 0 ? count : 0
}

function compareAlphabetically(
  left: ImplantCatalogOption,
  right: ImplantCatalogOption
): number {
  return `${left.brand} ${left.name} ${left.source_code ?? ''}`.localeCompare(
    `${right.brand} ${right.name} ${right.source_code ?? ''}`,
    'it',
    { sensitivity: 'base', numeric: true }
  )
}

function compareByUsage(
  left: ImplantCatalogOption,
  right: ImplantCatalogOption
): number {
  return usageCount(right) - usageCount(left) || compareAlphabetically(left, right)
}

/**
 * Un'opzione compare in un solo gruppo: prima i preferiti, poi gli impianti
 * più usati e infine il resto del catalogo in ordine alfabetico.
 */
export function groupImplantCatalogOptions(
  options: readonly ImplantCatalogOption[],
  searchTerm: string,
  popularLimit = DEFAULT_POPULAR_IMPLANT_LIMIT
): ImplantCatalogOptionGroup[] {
  const filtered = options.filter((option) =>
    matchesImplantCatalogOption(option, searchTerm)
  )

  const favorites = filtered
    .filter((option) => option.is_favorite === true)
    .sort(compareByUsage)

  const favoriteIds = new Set(favorites.map((option) => option._id))
  const safePopularLimit = Math.max(0, Math.trunc(popularLimit))
  const popular = filtered
    .filter(
      (option) => !favoriteIds.has(option._id) && usageCount(option) > 0
    )
    .sort(compareByUsage)
    .slice(0, safePopularLimit)

  const promotedIds = new Set([
    ...favoriteIds,
    ...popular.map((option) => option._id),
  ])
  const all = filtered
    .filter((option) => !promotedIds.has(option._id))
    .sort(compareAlphabetically)

  return [
    { key: 'favorites' as const, label: 'Preferiti', options: favorites },
    { key: 'popular' as const, label: 'Più usati', options: popular },
    { key: 'all' as const, label: 'Tutti gli impianti', options: all },
  ].filter((group) => group.options.length > 0)
}

